import React, { useState, useContext } from 'react';
import { TextField, Button, MenuItem,Box,Typography, Dialog, DialogContent, DialogActions, Collapse} from '@mui/material';
import { ProjectContext } from '../../../layouts/CreateProjectLayout';
import modelTasks from '../../../constants/modelTasks.json';
import modelFrameworks from '../../../constants/modelFrameworks.json';
import axios from 'axios';

const TrainingFormModal = ({ open, onClose, onTrainingCreated }) => {
  const { project, projectTrainings, fetchProjectTrainings } = useContext(ProjectContext);
  const [showParameters, setShowParameters] = useState(false);
  const [isNameEdited, setIsNameEdited] = useState(false);

  const generateModelName = (formData) => {
  const { modelTask, modelFramework, imgsz, batchSize, epochs } = formData;
  if (!modelTask || !modelFramework) return "";
  return `$imgsz${imgsz || 'NA'}_batch${batchSize || 'NA'}_epoch${epochs || 'NA'}`;
};

  const [form, setForm] = useState({
    modelTask: '',
    modelFramework: '',
    imgsz: '',
    batchSize: '',
    epochs: '',
    modelName: '',
  });

  const isFixedEpoch = form.modelFramework === "anomalib";
  const normalizedName = form.modelName.trim().toLowerCase();

  const isDuplicateName =
    normalizedName !== "" &&
    projectTrainings.some(
      t => t.info?.name?.toLowerCase().trim() === normalizedName
    );


  const handleChange = (e) => {
  const { name, value } = e.target;

  if (name === "modelName") {
    setIsNameEdited(true); // user manually typed
    setForm(prev => ({ ...prev, modelName: value }));
    return;
  }

  if (name === "modelFramework") {
    const selectedFramework = modelFrameworks.find(
      (framework) => framework.value === value
    );

    setForm((prev) => {
      const updatedForm = {
        ...prev,
        modelFramework: value,
        imgsz: selectedFramework?.imgz?.toString() || "",
        batchSize: selectedFramework?.batch?.toString() || "",
        epochs: value === "anomalib" ? "1" : selectedFramework?.epochs?.toString() || "",
      };

      // auto-generate model name if not manually edited
      if (!isNameEdited) {
        updatedForm.modelName = generateModelName(updatedForm);
      }

      return updatedForm;
    });
  } else {
    setForm(prev => {
      const updatedForm = {
        ...prev,
        [name]: value,
        ...(name === "modelTask" && { modelFramework: "" }), // reset framework if task changes
      };

      if (!isNameEdited) {
        updatedForm.modelName = generateModelName(updatedForm);
      }

      return updatedForm;
    });
  }
};

  const handleTrain = async () => {
    if (isDuplicateName) {
  alert("Training name already exists. Please choose another name.");
  return;
}

      if (!form.modelTask || !form.modelFramework || !form.modelName) {
        alert("Please fill all required fields.");
        return;
      }
      if (form.batchSize && parseInt(form.batchSize, 10) <= 0) {
        alert("Batch size must be a positive number.");
        return;
      }
      if (form.epochs && parseInt(form.epochs, 10) <= 0) {
        alert("Epochs must be a positive number.");
        return;
      }
      if (form.imgsz && parseInt(form.imgsz, 10) <= 0) {
        alert("Image size must be a positive number.");
        return;
      }

    try {
      const trainingParams = { modelFramework: form.modelFramework, modelTask: form.modelTask };
      if (form.batchSize) trainingParams.batch = parseInt(form.batchSize, 10);
      if (form.epochs) trainingParams.epochs = parseInt(form.epochs, 10);
      if (form.imgsz) trainingParams.imgsz = parseInt(form.imgsz, 10);

      // 1. Create the model first
      const trainingPayload = {
        project_id: project.id,
        // name: form.modelName,
        info: {
          name: form.modelName,
          modelTask: form.modelTask,
          modelFramework: form.modelFramework,
          training_params: trainingParams
        },
      };

      const trainingResponse = await axios.post('/trainings/', trainingPayload);
      console.log('Training created:', trainingResponse.data);

      if (onTrainingCreated) onTrainingCreated(trainingResponse.data.id);

      // 3. Refresh training list after new model & training created
      await fetchProjectTrainings();

      // 4. Reset form and close modal
      setForm({
        modelTask: '',
        modelFramework: '',
        imgsz: '',
        batchSize: '',
        epochs: '',
        modelName: '',
      });
      onClose();
    } catch (error) {
      console.error('Error creating model or training:', error);
    }
  };

  const filteredFrameworks = form.modelTask
    ? modelFrameworks.filter(
        (framework) => framework.task.toLowerCase() === form.modelTask.toLowerCase()
      )
    : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth slotProps={{ paper: { sx: { border: '1px solid #cccccc', borderRadius: '12px' } } }}>
      <DialogContent>

          <Typography variant="h4" fontWeight={700} color="black" mt={1}>
            Model Training
          </Typography>


          <Box
            sx={{
              width: '95%',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: 2,
            }}
          >
            <Typography variant="h6" color="rgb(0, 0, 0)" mt={2}>
              Select Base Model
            </Typography>

            <TextField
              select
              label="Model Task"
              name="modelTask"
              value={form.modelTask}
              onChange={handleChange}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            >
              <MenuItem value="">None</MenuItem>
              {modelTasks.map((task) => (
                <MenuItem key={task.value} value={task.value}>
                  {task.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Select Model"
              name="modelFramework"
              value={form.modelFramework}
              onChange={handleChange}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            >
              {filteredFrameworks.length === 0 ? (
                <MenuItem value="">No frameworks available</MenuItem>
              ) : (
                filteredFrameworks.map((framework) => (
                  <MenuItem key={framework.value} value={framework.value}>
                    {framework.label}
                  </MenuItem>
                ))
              )}
            </TextField>

            {/* <Typography variant="h6" color="rgb(0, 0, 0)" mt={2}>
              Parameters
            </Typography> */}
            <Typography
              variant="h6"
              color="rgb(0, 0, 0)"
              mt={2}
              sx={{ cursor: 'pointer' }}
              onClick={() => setShowParameters(prev => !prev)}
            >
              Parameters {showParameters ? '▲' : '▼'}
            </Typography>

              <Collapse in={showParameters}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <Typography variant="subtitle2" color="black">Training parameters</Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      type="number"
                      label="Image size (px)"
                      name="imgsz"
                      value={form.imgsz}
                      onChange={handleChange}
                      fullWidth
                      autoComplete="off"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                    <TextField
                      type="number"
                      label="Batch size"
                      name="batchSize"
                      value={form.batchSize}
                      onChange={handleChange}
                      fullWidth
                      autoComplete="off"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                    <TextField
                      type="number"
                      label="Epochs"
                      name="epochs"
                      value={isFixedEpoch ? "1" : form.epochs}
                      onChange={handleChange}
                      fullWidth
                      autoComplete="off"
                      disabled={isFixedEpoch}
                      sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                        '& .Mui-disabled': { backgroundColor: '#f5f5f5', color: '#666' },
                      }}
                    />
                  </Box>

                  <Typography variant="subtitle2" color="black">Training name</Typography>
                <TextField
                  label="Training name"
                  name="modelName"
                  value={form.modelName}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  error={isDuplicateName}
                  helperText={isDuplicateName ? "Training name already exists" : ""}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
                </Box>
              </Collapse>

          </Box>
        {/* </Box> */}
      </DialogContent>

      <DialogActions>
        <Box
          sx={{
            padding: 2,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            gap: 4,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: '16px',
              width: '120px',
              color: '#16a4b0',
              borderColor: '#16a4b0',
              '&:hover': {
                backgroundColor: '#f0f0f0',
                borderColor: '#16a4b0',
              },
            }}
          >
            Cancel
          </Button>

<Button
  variant="contained"
  onClick={handleTrain}
  disabled={isDuplicateName}
  sx={{
    borderRadius: '12px',
    backgroundColor: '#16a4b0',
    color: 'white',
    textTransform: 'none',
    fontSize: '16px',
    width: '120px',
    '&:hover': { backgroundColor: '#00707c' },
  }}
>
  Train
</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TrainingFormModal;
